import * as React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import CompletionOdometerField, { EMPTY_ODOMETER, odometerAllowsSubmit, odometerPayload } from './CompletionOdometerField';
import { needsOdometerPrompt } from './stateTransitions';

/**
 * `maintenance` (the detail object) and `odometerRequired` are optional: when given and the
 * transition finishes a work order that has no reading of its own, the dialog asks for the
 * odometer reading and passes it to onConfirm({ reading_km, reading_hours }).
 */
function ConfirmStateChangeDialog({
  t, open, handleClose, onConfirm, currentState, targetState, hasActiveBooking, invoiceReopenInfo,
  maintenance = null, odometerRequired = false,
}) {
  const isReopen = ['completed', 'picked_up'].includes(currentState) && targetState === 'in_progress';
  const showOdometer = open && needsOdometerPrompt(maintenance, targetState);
  const [odometer, setOdometer] = React.useState(EMPTY_ODOMETER);

  React.useEffect(() => {
    if (open) setOdometer(EMPTY_ODOMETER);
  }, [open, maintenance?.id, targetState]);

  // Determine if this is a backward transition
  const stateOrder = ['quote', 'planned', 'in_progress', 'completed', 'picked_up'];
  const currentIndex = stateOrder.indexOf(currentState);
  const targetIndex = stateOrder.indexOf(targetState);
  const isSideState = ['waiting', 'vehicle_in_workshop'].includes(targetState) ||
    ['waiting', 'vehicle_in_workshop'].includes(currentState);
  const isBackward = !isSideState && targetIndex < currentIndex && targetState !== 'cancelled';
  const isCancellation = targetState === 'cancelled';

  // Get dialog content based on transition type
  const getDialogContent = () => {
    if (isCancellation) {
      return {
        title: t('Cancel maintenance?'),
        message: t('This will cancel the maintenance. This action can be difficult to undo.'),
        icon: <CancelIcon sx={{ fontSize: 48, color: 'error.main' }} />,
        confirmLabel: t('Yes, cancel maintenance'),
        confirmColor: 'error',
      };
    }

    // Side state transitions
    if (isSideState) {
      const sideMessages = {
        'planned_to_waiting': {
          title: t('Set as waiting for parts?'),
          message: t('This will mark the maintenance as waiting for parts.'),
          icon: <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
          confirmLabel: t('Yes, set waiting'),
          confirmColor: 'warning',
        },
        'in_progress_to_waiting': {
          title: t('Set as waiting for parts?'),
          message: t('This will pause the maintenance while waiting for parts.'),
          icon: <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
          confirmLabel: t('Yes, set waiting'),
          confirmColor: 'warning',
        },
        'planned_to_vehicle_in_workshop': {
          title: t('Mark vehicle in workshop?'),
          message: t('This will mark that the vehicle has been brought to the workshop.'),
          icon: <PlayArrowIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
          confirmLabel: t('Yes, mark arrived'),
          confirmColor: 'primary',
        },
        'waiting_to_in_progress': {
          title: t('Continue work?'),
          message: t('This will resume the maintenance. Parts have arrived and work can continue.'),
          icon: <PlayArrowIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
          confirmLabel: t('Yes, continue'),
          confirmColor: 'primary',
        },
        'waiting_to_planned': {
          title: t('Return to planned?'),
          message: t('This will move the maintenance back to planned status.'),
          icon: <ReplayIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
          confirmLabel: t('Yes, go back'),
          confirmColor: 'warning',
        },
        'vehicle_in_workshop_to_planned': {
          title: t('Return to planned?'),
          message: t('This will move the maintenance back to planned status.'),
          icon: <ReplayIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
          confirmLabel: t('Yes, go back'),
          confirmColor: 'warning',
        },
        'vehicle_in_workshop_to_in_progress': {
          title: t('Start maintenance?'),
          message: t('This will start work on the vehicle that is already in the workshop.'),
          icon: <PlayArrowIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
          confirmLabel: t('Yes, start'),
          confirmColor: 'primary',
        },
      };

      const key = `${currentState}_to_${targetState}`;
      return sideMessages[key] || {
        title: t('Change status?'),
        message: t('Are you sure you want to change the maintenance status?'),
        icon: <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
        confirmLabel: t('Yes, change'),
        confirmColor: 'primary',
      };
    }

    if (isBackward) {
      const messages = {
        'planned_to_quote': {
          title: t('Return to quote?'),
          message: t('This will revert the maintenance back to quote status. Any schedules and employee assignments will be removed.'),
        },
        'in_progress_to_planned': {
          title: t('Return to planned?'),
          message: t('This will move the maintenance back to planned status. Any progress tracking will be preserved.'),
        },
        'completed_to_in_progress': {
          title: t('Reopen maintenance?'),
          message: t('This will reopen the maintenance for additional work.'),
        },
        'picked_up_to_in_progress': {
          title: t('Reopen maintenance?'),
          message: t('This will reopen the maintenance. The vehicle has already been marked as picked up.'),
        },
      };

      const key = `${currentState}_to_${targetState}`;
      const content = messages[key] || {
        title: t('Go back?'),
        message: t('Are you sure you want to change the maintenance status?'),
      };

      return {
        ...content,
        icon: <ReplayIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
        confirmLabel: t('Yes, go back'),
        confirmColor: 'warning',
      };
    }

    // Forward transitions
    const forwardMessages = {
      'quote_to_planned': {
        title: t('Convert to maintenance?'),
        message: t('This will convert the quote into an active maintenance.'),
        icon: <PlayArrowIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
        confirmLabel: t('Yes, convert'),
        confirmColor: 'primary',
      },
      'planned_to_in_progress': {
        title: t('Start maintenance?'),
        message: t('This will mark the maintenance as in progress. The work on this vehicle is starting.'),
        icon: <PlayArrowIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
        confirmLabel: t('Yes, start'),
        confirmColor: 'primary',
      },
      'in_progress_to_completed': {
        title: t('Complete maintenance?'),
        message: t('This will mark the maintenance as completed. Make sure all work is finished.'),
        icon: <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />,
        confirmLabel: t('Yes, complete'),
        confirmColor: 'success',
      },
    };

    const key = `${currentState}_to_${targetState}`;
    return forwardMessages[key] || {
      title: t('Change status?'),
      message: t('Are you sure you want to change the maintenance status?'),
      icon: <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
      confirmLabel: t('Yes, change'),
      confirmColor: 'primary',
    };
  };

  const content = getDialogContent();

  const odometerBlocks = showOdometer && !odometerAllowsSubmit(odometer, odometerRequired);

  const handleConfirm = () => {
    onConfirm(showOdometer ? odometerPayload(odometer).payload : {});
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
        <Box sx={{ mb: 2 }}>
          {content.icon}
        </Box>
        {content.title}
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        <DialogContentText sx={showOdometer ? { mb: 2 } : undefined}>
          {content.message}
        </DialogContentText>
        {showOdometer && (
          <CompletionOdometerField
            maintenance={maintenance}
            required={odometerRequired}
            value={odometer}
            onChange={setOdometer}
          />
        )}
        {isCancellation && hasActiveBooking && (
          <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
            {t('booking_linked_cancel_warning')}
          </Alert>
        )}
        {isReopen && invoiceReopenInfo?.hasKoontilasku && (
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
            {t('reopen_blocked_by_koontilasku')}
          </Alert>
        )}
        {isReopen && !invoiceReopenInfo?.hasKoontilasku && invoiceReopenInfo?.hasBlockingInvoices && (
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
            {t('reopen_blocked_by_invoices', { numbers: invoiceReopenInfo.blockingInvoiceNumbers.join(', ') })}
          </Alert>
        )}
        {isReopen && !invoiceReopenInfo?.hasKoontilasku && !invoiceReopenInfo?.hasBlockingInvoices && invoiceReopenInfo?.hasDraftInvoices && (
          <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
            {t('reopen_will_cancel_draft_invoices', { numbers: invoiceReopenInfo.draftInvoiceNumbers.join(', ') })}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1, justifyContent: 'center' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: '10px',
            px: 3,
            py: 1,
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.primary',
              bgcolor: 'transparent',
            },
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={content.confirmColor}
          disabled={odometerBlocks || (isReopen && (invoiceReopenInfo?.hasKoontilasku || invoiceReopenInfo?.hasBlockingInvoices))}
          sx={{
            borderRadius: '10px',
            px: 3,
            py: 1,
          }}
        >
          {content.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(ConfirmStateChangeDialog);
